import React from "react";

const attentionWeeks = [
  {
    emoji: "👀",
    title: "OBSERVE",
    phase: "AWARENESS",
    color: "from-cyan-500 to-blue-500",
    objective: "Understand current attention patterns.",
    outcome: "📝 Attention Observation Log",
  },
  {
    emoji: "🔍",
    title: "IDENTIFY",
    phase: "AWARENESS",
    color: "from-yellow-500 to-orange-500",
    objective: "Discover distraction triggers.",
    outcome: "⚠️ Attention Trigger Map",
  },
  {
    emoji: "❤️",
    title: "CONNECT",
    phase: "AWARENESS",
    color: "from-pink-500 to-rose-500",
    objective: "Strengthen parent-child connection.",
    outcome: "🤝 Connection Score",
  },
  {
    emoji: "😴",
    title: "RESET",
    phase: "FOUNDATION",
    color: "from-indigo-500 to-purple-500",
    objective: "Improve sleep and recovery.",
    outcome: "🌙 Sleep Stability Tracker",
  },
  {
    emoji: "🍎",
    title: "NOURISH",
    phase: "FOUNDATION",
    color: "from-green-500 to-emerald-500",
    objective: "Support brain and body health.",
    outcome: "🥗 Brain Fuel Score",
  },
  {
    emoji: "🏃",
    title: "MOVE",
    phase: "FOUNDATION",
    color: "from-teal-500 to-cyan-500",
    objective: "Build movement and energy regulation.",
    outcome: "⚡ Energy Regulation Tracker",
  },
  {
    emoji: "🎯",
    title: "ATTEND",
    phase: "FOCUS BUILDING",
    color: "from-orange-500 to-red-500",
    objective: "Train sustained attention.",
    outcome: "🎯 Focus Duration Baseline",
  },
  {
    emoji: "🧠",
    title: "ORGANIZE",
    phase: "FOCUS BUILDING",
    color: "from-purple-500 to-indigo-500",
    objective: "Build planning and organization skills.",
    outcome: "📋 Personal Routine System",
  },
  {
    emoji: "📵",
    title: "SIMPLIFY",
    phase: "FOCUS BUILDING",
    color: "from-gray-500 to-slate-500",
    objective: "Reduce attention fragmentation.",
    outcome: "🔕 Distraction Reduction Score",
  },
  {
    emoji: "💪",
    title: "ACHIEVE",
    phase: "CONFIDENCE",
    color: "from-green-500 to-lime-500",
    objective: "Create visible wins and momentum.",
    outcome: "🏆 Achievement Journal",
  },
  {
    emoji: "😊",
    title: "REGULATE",
    phase: "CONFIDENCE",
    color: "from-pink-500 to-purple-500",
    objective: "Build emotional self-regulation.",
    outcome: "❤️ Emotional Awareness Journal",
  },
  {
    emoji: "🦋",
    title: "EVOLVE",
    phase: "RENEWAL",
    color: "from-emerald-500 to-green-500",
    objective: "Create the next 90-day growth plan.",
    outcome: "🚀 Next Attention BOS Plan",
  },
];

const phaseColors = {
  AWARENESS: "border-cyan-500 bg-cyan-500/10",
  FOUNDATION: "border-green-500 bg-green-500/10",
  "FOCUS BUILDING": "border-orange-500 bg-orange-500/10",
  CONFIDENCE: "border-purple-500 bg-purple-500/10",
  RENEWAL: "border-emerald-500 bg-emerald-500/10",
};

const PDAttentionBOS = () => {
  return (
    <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
      <div className="relative overflow-hidden bg-gradient-to-br from-cyan-950 via-blue-950 to-black p-8 lg:p-12">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_#06b6d4,_transparent_40%)]"></div>

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full border border-cyan-700 bg-cyan-900/40 text-xs font-semibold mb-2">
            🚪 PurpleDoor Behavioral Operating System
          </div>

          <h1 className="text-lg lg:text-xl text-white leading-tight">
            🧠 Attention & Focus BOS
          </h1>

          <h2 className="text-xs lg:text-sm text-cyan-300 mt-1 font-bold">
            Awareness → Structure → Habits → Focus → Confidence → Growth
          </h2>

          <p className="max-w-4xl mt-2 text-gray-200 text-xs lg:text-sm leading-relaxed">
            Not A Diagnosis Program.
            <br />
            Not A Treatment Program.
            <br />A Family-Driven Attention & Focus Improvement Operating
            System.
          </p>

          <div className="grid lg:grid-cols-5 gap-2 mt-4">
            <div className="bg-black/30 border border-cyan-700 rounded-lg p-2">
              <div className="text-lg lg:text-xl">👀</div>
              <div className="font-bold text-xs lg:text-sm mt-2">AWARENESS</div>
              <div className="text-xs lg:text-sm text-gray-200">Weeks 1-3</div>
            </div>

            <div className="bg-black/30 border border-green-700 rounded-lg p-2">
              <div className="text-lg lg:text-xl">🏗️</div>
              <div className="font-bold text-xs lg:text-sm mt-2">FOUNDATION</div>
              <div className="text-xs lg:text-sm text-gray-200">Weeks 4-6</div>
            </div>

            <div className="bg-black/30 border border-orange-700 rounded-lg p-2">
              <div className="text-lg lg:text-xl">🎯</div>
              <div className="font-bold text-xs lg:text-sm mt-2">FOCUS</div>
              <div className="text-xs lg:text-sm text-gray-200">Weeks 7-9</div>
            </div>

            <div className="bg-black/30 border border-purple-700 rounded-lg p-2">
              <div className="text-lg lg:text-xl">💪</div>
              <div className="font-bold text-xs lg:text-sm mt-2">CONFIDENCE</div>
              <div className="text-xs lg:text-sm text-gray-200">Weeks 10-11</div>
            </div>

            <div className="bg-black/30 border border-emerald-700 rounded-lg p-2">
              <div className="text-lg lg:text-xl">🦋</div>
              <div className="font-bold text-xs lg:text-sm mt-2">RENEWAL</div>
              <div className="text-xs lg:text-sm text-gray-200">Week 12</div>
            </div>
          </div>
        </div>
      </div>

      {/* 12 WEEK JOURNEY */}

      <div className="p-2">
        <div className="text-center mb-10">
          <h2 className="text-lg lg:text-xl text-cyan-300">
            🌟 12 Week Attention Transformation Journey
          </h2>

          <p className="text-gray-200 text-sm lg:text-lg mt-2">
            Better Attention. Better Habits. Better Outcomes.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          {attentionWeeks.map((week, index) => (
            <div
              key={index}
              className={`
            rounded-xl
            border
            ${phaseColors[week.phase]}
            backdrop-blur-md
            overflow-hidden
            hover:scale-[1.02]
            transition-all
            duration-300
          `}
            >
              <div className={`bg-gradient-to-r ${week.color} p-2`}>
                <div className="flex justify-between items-center">
                  <div className="text-lg lg:text-xl">{week.emoji}</div>

                  <div className="text-xs lg:text-sm font-bold bg-black/30 px-3 py-1 rounded-full">
                    WEEK {index + 1}
                  </div>
                </div>

                <h3 className="text-sm lg:text-lg text-white mt-1">{week.title}</h3>
              </div>

              <div className="p-2">
                <div className="text-xs lg:text-sm text-cyan-300 font-bold mb-1">
                  {week.phase}
                </div>

                <p className="text-xs lg:text-sm text-gray-200 mb-2">{week.objective}</p>

                <div className="bg-black/20 rounded-lg p-3 border border-white/10">
                  <div className="text-xs lg:text-sm text-gray-200 mb-1">OUTCOME</div>

                  <div className="font-semibold text-xs lg:text-sm text-green-300">
                    {week.outcome}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BEFORE AFTER */}

      <div className="grid lg:grid-cols-2 gap-6 px-2 pb-2">
        <div className="bg-red-950/20 border border-red-800 rounded-xl p-2">
          <h3 className="text-sm lg:text-lg text-red-300 mb-2">😵 BEFORE</h3>

          <div className="space-y-3 text-xs lg:text-sm text-gray-200">
            <div>📱 Excessive Screen Dependence</div>
            <div>⚡ Easily Distracted</div>
            <div>😴 Poor Sleep Habits</div>
            <div>🌀 Impulsive Behaviour</div>
            <div>📉 Weak Focus</div>
            <div>😔 Low Confidence</div>
          </div>
        </div>

        <div className="bg-green-950/20 border border-green-800 rounded-xl p-2">
          <h3 className="text-sm lg:text-lg text-green-300 mb-2">🚀 AFTER</h3>

          <div className="space-y-3 text-xs lg:text-sm text-gray-200">
            <div>🎯 Better Attention</div>
            <div>🧠 Improved Focus</div>
            <div>😴 Better Sleep</div>
            <div>😊 Better Self-Regulation</div>
            <div>💪 Increased Confidence</div>
            <div>📈 Better Outcomes</div>
          </div>
        </div>
      </div>

      <div className="px-2 pb-2">
        <div className="rounded-xl border border-cyan-700 bg-gradient-to-r from-cyan-950 via-blue-950 to-indigo-950 p-8">
          <h2 className="text-lg lg:text-xl text-white text-center mb-8">
            🚪 The PurpleDoor Difference
          </h2>

          <div className="max-w-5xl mx-auto text-center">
            <p className="text-xs lg:text-sm text-gray-100 leading-relaxed">
              Attention Is A Skill.
            </p>

            <p className="text-xs lg:text-sm text-gray-100 leading-relaxed">
              Focus Is Trainable.
            </p>

            <p className="text-xs lg:text-sm text-gray-100 leading-relaxed">
              Habits Can Change.
            </p>

            <p className="text-xs lg:text-sm text-gray-100 leading-relaxed">
              Progress Is Possible.
            </p>

            <div className="my-4 text-lg lg:text-xl">⬇️</div>

            <h3 className="text-lg lg:text-xl text-cyan-300">
              Repeat Every 90 Days
            </h3>

            <p className="mt-2 text-xs lg:text-sm text-gray-200 max-w-3xl mx-auto">
              Small Actions → Daily Consistency → Better Habits → Improved Focus
              → Higher Confidence → Better Outcomes
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}

      <div className="bg-gradient-to-r from-cyan-900 via-blue-900 to-indigo-950 p-8 text-center">
        <div className="text-sm lg:text-lg mb-2">🧠</div>

        <h2 className="text-sm lg:text-lg text-white mb-2">
          PurpleDoor Attention & Focus BOS
        </h2>

        <p className="text-sm lg:text-lg text-cyan-200">
          Awareness → Structure → Habits → Focus → Confidence → Growth
        </p>

        <p className="text-xs lg:text-sm mt-2 text-gray-200 max-w-4xl mx-auto">
          Every 90 Days, A Better Version Of The Child.
          <br />
          A Better Version Of The Family.
          <br />A Better Future For Everyone.
        </p>
      </div>
    </div>
  );
};

export default PDAttentionBOS;
